import { FieldError } from "@components/form/FieldError";
import { PhoneInput as UIPhoneInput } from "@thetreep/ui";
import {
  type CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface PhoneProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export function PhoneInput({ value, error, onChange }: PhoneProps) {
  const { t } = useTranslation();
  const defaultCountry = {
    dialCode: "+33",
    id: "FR",
    label: "France",
  };

  const [country, setCountry] = useState(defaultCountry);
  const parsed = value ? parsePhoneNumberFromString(value) : undefined;
  if (parsed?.country) {
    const valueDialCode = `+${getCountryCallingCode(parsed.country)}`;
    if (valueDialCode !== country.dialCode) {
      setCountry({
        dialCode: valueDialCode,
        id: parsed.country,
        label: parsed.country,
      });
    }
  }

  const phone =
    parsed?.nationalNumber ??
    (value ? value.substring(country.dialCode.length) : "");

  const liveError =
    phone.length > 0 && !isValidPhoneNumber(value, country.id as CountryCode)
      ? t("form.phone.invalid")
      : undefined;
  const displayedError = error ?? liveError;

  function formatPhone(phone: string): string {
    const digitsOnly = phone.replace(/\D/g, "");
    let formattedPhone = "";
    if (digitsOnly.length > 0) {
      formattedPhone = digitsOnly[0];
      for (let i = 1; i < digitsOnly.length; i += 2) {
        formattedPhone += " ";
        formattedPhone += digitsOnly.substring(i, i + 2);
      }
    }
    return formattedPhone;
  }

  const handlePhoneChange = (newPhone: string) => {
    onChange(country.dialCode + newPhone);
  };

  return (
    <>
      <div
        className="border border-gray-200 p-[6px] rounded-[8px] mt-[10px] 
                                                        [&_input]:border-none [&_input]:m-0"
      >
        <UIPhoneInput // we need to cleanup that component props...
          overrides={{
            Root: {
              props: {
                "data-testid": "search-passenger-phone",
              },
            },
          }}
          text={formatPhone(phone)}
          onTextChange={(e) => {
            const cleanedPhone = e.target.value.replace(/\D/g, "");
            handlePhoneChange(cleanedPhone);
          }}
          country={country as any} // eslint-disable-line
          onCountryChange={(e) => {
            const newCountry = e.value[0] as any; // eslint-disable-line
            setCountry(newCountry);
            onChange(newCountry.dialCode + phone);
          }}
          placeholder="6 00 00 00 00"
          error={!!displayedError}
        />
      </div>
      <FieldError error={displayedError} />
    </>
  );
}
